import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators'; import { MessageService } from 'primeng/api';
import { CartShopifyService } from 'src/app/services/cart-shopify.service';
import { CartNuvemShopService } from 'src/app/services/cart-nuvem-shop.service';
import { ShoppingCartService } from 'src/app/services/shopping-cart.service';
@Injectable({ providedIn: 'root' })
export class CartFacadeService {
  constructor(
    private shopifyService: CartShopifyService,      // já usados hoje
    private nuvemShopService: CartNuvemShopService,  // idem
    private cartService: ShoppingCartService,
    private messageService: MessageService
  ) { }

  // -------- utils compartilhados --------
  extractShopifyProductId(raw: any): number {
    if (!raw) return 0;
    const s = String(raw);
    const m = s.match(/(\d+)$|gid:\/\/shopify\/ProductVariant\/(\d+)/);
    return m ? Number(m[1] || m[2]) : Number(s.replace(/\D/g, ''));
  }


  toCartItemFromProduct(p: any, qty = 1) {
    const variant = p?.selectedVariant as any;
    const source = variant || p;

    // -------- helpers --------
    const normalizeId = (raw: any): string => {
      if (raw == null) return '';
      const str = String(raw);
      const match = str.match(/(\d+)$/);
      return match ? match[1] : str.replace(/\D/g, '');
    };

    const pickVariantId = (v: any, parent: any): string => {
      const raw =
        v?.variant_id ??
        v?.id ??
        v?.selected_variant?.id ??
        v?.selected_or_first_available_variant?.id ??
        parent?.variant_id ??
        parent?.id ??
        parent?.sku;

      return normalizeId(raw);
    };

    const pickProductId = (v: any, parent: any): number | null => {
      const raw =
        parent?.product_id ??
        parent?.id ??
        v?.product_id ??
        v?.product?.id ??
        null;

      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const isAutoVariantTitle = (v: any) => {
      const s = String(v ?? '').trim();
      return /^variant\s*#\d+$/i.test(s) || s.toLowerCase().startsWith('variant #');
    };

    // ✅ NOVO: array de variações (pra /comprar enviar variation[0], variation[1]...)
    const buildVariationArray = (v: any): string[] => {
      // Shopify
      if (Array.isArray(v?.selectedOptions) && v.selectedOptions.length) {
        return v.selectedOptions
          .map((o: any) => String(o?.value ?? '').trim())
          .filter(Boolean);
      }

      // Nuvemshop (muito comum vir assim no cart/product)
      if (Array.isArray(v?.variant_values) && v.variant_values.length) {
        return v.variant_values.map((x: any) => String(x).trim()).filter(Boolean);
      }

      if (Array.isArray(v?.options) && v.options.length) {
        return v.options.map((x: any) => String(x).trim()).filter(Boolean);
      }

      const o1 = v?.option1 ?? v?.option_1;
      const o2 = v?.option2 ?? v?.option_2;
      const o3 = v?.option3 ?? v?.option_3;

      return [o1, o2, o3].filter(Boolean).map((x) => String(x).trim());
    };

    // ✅ label "humana" pra nome do item (você tinha isso antes)
    const buildVariantLabel = (v: any): string => {
      // 1) Shopify style
      if (Array.isArray(v?.selectedOptions) && v.selectedOptions.length) {
        return v.selectedOptions.map((o: any) => o.value).join(' / ');
      }

      // 2) Nuvemshop style
      if (Array.isArray(v?.variant_values) && v.variant_values.length) {
        return v.variant_values.join(' / ');
      }

      if (typeof v?.short_variant_name === 'string' && v.short_variant_name.trim()) {
        return v.short_variant_name.replace(/[()]/g, '').trim();
      }

      if (Array.isArray(v?.options) && v.options.length) {
        return v.options.filter(Boolean).join(' / ');
      }

      const o1 = v?.option1 ?? v?.option_1;
      const o2 = v?.option2 ?? v?.option_2;
      const o3 = v?.option3 ?? v?.option_3;
      const arr = [o1, o2, o3].filter((x) => !!x).map((x) => String(x).trim());
      if (arr.length) return arr.join(' / ');

      // 3) fallback: title se for “humano”
      if (v?.title && !isAutoVariantTitle(v.title)) {
        return String(v.title).trim();
      }

      return '';
    };

    // -------- ids --------
    const productId = pickProductId(source, p);
    const variantId = pickVariantId(source, p);

    // ✅ NOVO: variações para Nuvemshop (/comprar)
    const variations = buildVariationArray(source);

    // -------- name --------
    const baseName = String(p?.name ?? p?.title ?? '').trim();
    const variantLabel = buildVariantLabel(source);
    const name = baseName;

    // -------- image/price/stock --------
    const image =
      source?.thumbnail ??
      source?.image ??
      p?.thumbnail ??
      p?.mainImage;

    const price =
      (source?.price != null ? Number(source.price) : null) ??
      (p?.price != null ? Number(p.price) : 0);

    const inventoryQty = Number(source?.inventory_quantity ?? source?.stock ?? 0);
    const tracked = !!source?.tracked;

    const categories = this.normalizeCategoryList(p?.categories ?? p?.category);

    return {
      // ✅ importante: ID do item = variante (pra não colidir entre variantes)
      id: variantId,

      quantity: qty,
      name,
      variant: variantLabel || undefined,
      image,
      price,

      categories,
      category: categories.length ? categories[0] : undefined,

      // ✅ para Nuvemshop, guarde SEMPRE o variant_id numérico/string numérica
      variant_id: variantId,

      // ✅ produto pai
      product_id: productId,

      inventory_quantity: inventoryQty,
      stock: inventoryQty,
      tracked,

      // ✅ AQUI é “no retorno do item” (é isso que eu quis dizer)
      // Serve pra você passar pro addToCartStorefront e montar variation[0], variation[1]...
      variations,
    };
  }



  private getItemsLabel(count: number): string {
    return count === 1 ? 'item' : 'itens';
  }

  private resolveErrorMessage(error: any): string {
    if (!error) return 'Ocorreu um erro ao adicionar os itens.';
    if (typeof error === 'string') return error;
    if (error.error) {
      if (typeof error.error === 'string') return error.error;
      if (typeof error.error === 'object') {
        return (
          error.error.description ||
          error.error.message ||
          (Array.isArray(error.error.errors) ? error.error.errors[0] : '') ||
          JSON.stringify(error.error)
        );
      }
    }
    if (error.message) return error.message;
    return 'Ocorreu um erro ao adicionar os itens.';
  }

  // -------- 1 item --------
  async addOne(item: any, platform: 'shopify' | 'nuvemshop' | 'local') {
    // ✅ Se a loja é Shopify mas o visitante NÃO está dentro do storefront Shopify,
    // não podemos chamar `/cart/add.js`. Nesse caso, usa carrinho local (localStorage).
    if (platform === 'shopify' && !this.isRunningInsideShopifyStorefront()) {
      platform = 'local';
    }
    if (platform === 'shopify') {
      try {
        await firstValueFrom(this.shopifyService.addToCart(item));
      } catch (error) {
        const detail = this.resolveErrorMessage(error);
        this.messageService.add({ severity: 'warn', summary: 'Não foi possível adicionar', detail });
        throw error;
      }
      console.log('em addOne')
      const cart = await firstValueFrom(this.shopifyService.getCartDetails());
      this.cartService.syncShopifyCart(cart.items, [item]);
      const total = cart.items?.reduce((t: number, i: any) => t + (i.quantity ?? 0), 0) ?? 0;
      this.messageService.add({ severity: 'success', summary: 'Produto Adicionado', detail: item.name });
      return total;
    }

    if (platform === 'nuvemshop') {
      this.cartService.addToCart(item, item.quantity, item.image);
      const total = this.cartService.getCartItems().reduce((t: number, it: any) => t + (it.quantity ?? 0), 0);
      this.cartService.updateCartCount(total);
      this.cartService.refreshCartState();
      this.messageService.add({ severity: 'success', summary: 'Produto Adicionado', detail: item.name });
      return total;
    }



    // local
    this.cartService.addToCart(item, item.quantity, item.image);
    const total = this.cartService.getCartItems().reduce((t: number, it: any) => t + (it.quantity ?? 0), 0);
    this.cartService.updateCartCount(total);
    this.messageService.add({ severity: 'success', summary: 'Produto Adicionado', detail: item.name });
    return total;
  }

  // -------- vários itens --------
  async addMany(items: any[], platform: 'shopify' | 'nuvemshop' | 'local') {
    if (!items?.length) return { itemsAdded: 0, total: 0 };
    console.log('em addMany de cart-service', platform)
    console.log(items)

    // ✅ Se a loja é Shopify mas o visitante NÃO está dentro do storefront Shopify,
    // não podemos chamar `/cart/add.js`. Nesse caso, usa carrinho local (localStorage).
    if (platform === 'shopify' && !this.isRunningInsideShopifyStorefront()) {
      platform = 'local';
    }

    if (platform === 'shopify') {
      const hasBatch = typeof (this.shopifyService as any).addManyToCart === 'function';
      try {
        if (hasBatch) {
          await firstValueFrom((this.shopifyService as any).addManyToCart(items));
        } else {
          const responses = await firstValueFrom(
            forkJoin(items.map(it =>
              this.shopifyService.addToCart(it).pipe(catchError(err => of({ error: err })))
            ))
          );
          const errors = (responses as any[]).filter(res => res && res.error);
          if (errors.length) {
            const detail = this.resolveErrorMessage(errors[0].error);
            throw new Error(detail);
          }
        }
      } catch (error) {
        const detail = this.resolveErrorMessage(error);
        throw new Error(detail);
      }
      console.log('em addOne')
      const cart = await firstValueFrom(this.shopifyService.getCartDetails());
      console.log('antes de persistir')
      console.log(cart.items)
      this.cartService.syncShopifyCart(cart.items, items);
      this.cartService.refreshCartState();
      const total = cart.items?.reduce((t: number, i: any) => t + (i.quantity ?? 0), 0) ?? 0;
      return { itemsAdded: items.length, total };
    }

    if (platform === 'nuvemshop') {
      if (this.isRunningInsideNuvemshopStorefront()) {
        console.log('isRunningInsideNuvemshopStorefront');

        for (const it of items) {
          const productId = Number(it.product_id);

          // ✅ sempre normalize o id solicitado (string -> number)
          const requestedVariantId = Number(
            String(it.variant_id || it.id).match(/(\d+)$/)?.[1] || 0
          );

          const qty = Number(it.quantity || 1);

          if (!productId || !requestedVariantId) {
            throw new Error('Produto inválido para adicionar ao carrinho.');
          }

          const resp = await firstValueFrom(
            this.nuvemShopService.addToCartStorefront({
              product_id: productId,
              variant_id: requestedVariantId,
              quantity: qty,
              variations: Array.isArray((it as any).variations) ? (it as any).variations : [],
            })
          );


          console.log('dps do comprar ');
          console.log(resp);

          const ok = resp.status >= 200 && resp.status < 400;
          if (!ok) {
            throw new Error('Falha ao adicionar produto ao carrinho.');
          }

          const body = resp.body;

          // ✅ erro retornado pelo JSON
          if (body?.success === false) {
            const msg = this.nuvemShopService.humanizeNuvemshopError(
              body,
              'Não foi possível adicionar ao carrinho.'
            );
            throw new Error(msg);
          }

          // ✅ se não veio cart, força refresh pra preencher artifacts (token/abandoned/map)
          if (!body?.cart) {
            await firstValueFrom(this.nuvemShopService.refreshCartArtifactsStorefront());
          }

          // ✅ ids reais retornados
          const lineItemId = Number(body?.item?.id || 0);
          const returnedVariantId = Number(body?.item?.variant_id || 0);

          if (!lineItemId || !returnedVariantId) {
            throw new Error('Nuvemshop: resposta inválida ao adicionar no carrinho.');
          }

          // ✅ VALIDAR: se a Nuvemshop adicionou outra variante, NÃO corrompe o carrinho local
          if (returnedVariantId && returnedVariantId !== requestedVariantId) {
            console.error('[NUVEMSHOP] Variante retornada diferente da solicitada', {
              requestedVariantId,
              returnedVariantId,
              productId,
              bodyItem: body?.item,
            });

            throw new Error(
              'A plataforma adicionou uma variação diferente da selecionada. Tente novamente.'
            );
          }

          // ✅ garante que o item local tenha o line_item_id correto (pra update/remove)
          (it as any).line_item_id = lineItemId;

          // ❌ NÃO faça mais isso (senão mistura variantes e soma qty errado)
          // it.variant_id = String(returnedVariantId);
          // it.id = String(returnedVariantId);

          // ✅ normaliza nome pro padrão "Produto - Variante"
          // body.item.name vem "Produto (Blanco)" -> converte pra "Produto - Blanco"
          if (typeof body?.item?.name === 'string' && body.item.name.trim()) {
            const rawName = body.item.name.trim();
            const m = rawName.match(/^(.*)\s+\((.*)\)\s*$/); // "X (Y)"
            if (m) {
              const base = m[1].trim();
              const label = m[2].trim();
              it.name = label ? `${base} - ${label}` : base;
            } else {
              it.name = rawName;
            }
          }

          // ✅ atualiza map: variant_id -> line_item_id (MERGE)
          const mapRaw = localStorage.getItem('ss_nuvemshop_line_item_map');
          const map = mapRaw ? JSON.parse(mapRaw) : {};
          map[String(returnedVariantId)] = lineItemId;
          localStorage.setItem('ss_nuvemshop_line_item_map', JSON.stringify(map));
        }
      }

      // ✅ só chega aqui se tudo deu certo (nenhum throw)
      for (const it of items) {
        this.cartService.addToCart(it, it.quantity, it.image);
      }

      const total = this.cartService
        .getCartItems()
        .reduce((t: number, it: any) => t + (it.quantity ?? 0), 0);

      this.cartService.updateCartCount(total);
      this.cartService.refreshCartState();

      return { itemsAdded: items.length, total };
    }


    // local
    for (const it of items) this.cartService.addToCart(it, it.quantity, it.image);
    const total = this.cartService.getCartItems().reduce((t: number, it: any) => t + (it.quantity ?? 0), 0);

    this.cartService.updateCartCount(total);
    this.cartService.refreshCartState();
    return { itemsAdded: items.length, total };
  }

  private isRunningInsideNuvemshopStorefront(): boolean {
    const LS = (window as any).LS;
    return !!(LS?.store);
  }

  private isRunningInsideShopifyStorefront(): boolean {
    // Heurística simples e consistente com ShoppingCartComponent.
    return (
      typeof (window as any).Shopify !== 'undefined' ||
      window.location.hostname.endsWith('.myshopify.com')
    );
  }

  private normalizeCategoryList(raw: any): string[] {
    if (!raw) return [];
    const entries = Array.isArray(raw) ? raw : [raw];
    return entries
      .map((entry: any) => {
        if (!entry) return null;
        if (typeof entry === 'string') return entry.trim();
        if (typeof entry === 'object') {
          const name = entry.title ?? entry.name;
          return name ? String(name).trim() : null;
        }
        return null;
      })
      .filter((value): value is string => !!value && value.length > 0);
  }





}
